import { FieldConfig, FieldType } from 'components/Forms/types'
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  Input,
  InputLabel,
  MenuItem,
  Select,
  SxProps,
  TextField,
  Typography,
} from '@mui/material'
import { compactInputSx, compactSelectSx } from './style'

type RenderProps = {
  id: string
  config: FieldConfig
  value: any
  onChange: (v: any) => void
  denseInputSx: SxProps
  containerEl?: HTMLElement | null
}

const CreativeField = ({ id, config, value, onChange, denseInputSx, containerEl }: RenderProps) => {
  switch (config.type) {
    case FieldType.TEXT:
      return (
        <TextField
          label={config.label}
          variant="standard"
          value={value ?? ''}
          onChange={e => onChange(e.target.value)}
          placeholder={config.placeholder}
          size="small"
          fullWidth
          sx={{ ...denseInputSx, ...compactInputSx }}
        />
      )

    case FieldType.NUMBER:
      return (
        <TextField
          label={config.label}
          type="number"
          variant="standard"
          value={value ?? ''}
          onChange={e => onChange(e.target.value === '' ? '' : Number(e.target.value))}
          size="small"
          fullWidth
          sx={{ ...denseInputSx, ...compactInputSx }}
        />
      )

    case FieldType.TEXTAREA:
      return (
        <TextField
          label={config.label}
          value={value ?? ''}
          variant="outlined"
          onChange={e => onChange(e.target.value)}
          placeholder={config.placeholder}
          size="small"
          fullWidth
          multiline
          minRows={3}
          // sx={{ ...denseInputSx, ...compactInputSx }}
        />
      )

    case FieldType.SELECT:
      return (
        <FormControl variant="standard" sx={{ ...denseInputSx, ...compactSelectSx }}>
          <InputLabel id="demo-simple-select-standard-label">{config.label}</InputLabel>
          <Select
            labelId="single-select-standard-label"
            id="single-select-standard"
            value={value ?? ''}
            placeholder={config.placeholder}
            onChange={e => onChange(e.target.value)}
            label={config.label}
            sx={{
              px: 0.5,
              '& .MuiSvgIcon-root': {
                display: 'inline-block',
              },
              backgroundColor: 'transparent',
            }}
          >
            {(config.options ?? []).map(opt => (
              <MenuItem
                key={opt}
                value={opt}
                sx={{
                  '&.Mui-selected:hover': { bgcolor: 'background.toolbar' },
                  '&:hover': { bgcolor: 'background.toolbar' },
                }}
              >
                <Typography fontSize={12}>{opt}</Typography>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )

    case FieldType.MULTISELECT:
      return (
        <FormControl
          fullWidth
          variant="standard"
          sx={{
            ...denseInputSx,
            ...compactSelectSx,
          }}
        >
          <InputLabel>{config.label}</InputLabel>

          <Select
            multiple
            value={Array.isArray(value) ? value : []}
            onChange={e => onChange(e.target.value)}
            renderValue={selected => (selected as string[]).join(', ')}
            input={
              <Input
                endAdornment={
                  <Typography variant="caption" sx={{ color: 'text.highlight' }}>
                    {(Array.isArray(value) ? value.length : 0) || 0}
                  </Typography>
                }
              />
            }
            sx={{
              '& .MuiSvgIcon-root': {
                display: 'none',
                fontSize: 12,
              },
            }} // keep your arrow hidden for this one
            MenuProps={{ disablePortal: true, container: containerEl, PaperProps: { 'data-in-drawer': true } }}
          >
            {(config.options ?? []).map(opt => {
              const isSelected = Array.isArray(value) && value.includes(opt)
              return (
                <MenuItem
                  key={opt}
                  value={opt}
                  dense
                  disableRipple
                  // the Select will set .Mui-selected automatically; this just tunes the look
                  sx={{
                    fontSize: 12,
                    '&.Mui-selected': {
                      bgcolor: 'background.help',
                      borderLeft: '3px solid',
                      borderColor: 'primary.main',
                      pl: 1, // compensate for the border
                    },
                    '&.Mui-selected:hover': { bgcolor: 'background.toolbar' },
                    '&:hover': { bgcolor: 'background.toolbar' },
                  }}
                >
                  {/* No checkbox—just the label */}
                  <Typography fontSize={12}>{opt}</Typography>
                </MenuItem>
              )
            })}
          </Select>
        </FormControl>

        // <FormControl fullWidth variant="standard" sx={{ ...denseInputSx, ...compactSelectSx }}>
        //   <InputLabel>{config.label}</InputLabel>
        //   <Select
        //     multiple
        //     value={Array.isArray(value) ? value : []}
        //     onChange={e => onChange(e.target.value)}
        //     renderValue={selected => (selected as string[]).join(', ')}
        //     input={
        //       <Input
        //         endAdornment={
        //           <Typography variant="caption" sx={{ color: 'text.highlight' }}>
        //             {(Array.isArray(value) ? value.length : 0) || 0}
        //           </Typography>
        //           // <InputAdornment position="start">
        //           // </InputAdornment>
        //         }
        //       />
        //     }
        //     sx={{ '& .MuiSvgIcon-root': { display: 'none' } }}
        //   >
        //     {(config.options ?? []).map(opt => (
        //       <MenuItem key={opt} value={opt}>
        //         <Checkbox size="small" checked={Array.isArray(value) && value.includes(opt)} />
        //         <Typography fontSize={12}>{opt}</Typography>
        //       </MenuItem>
        //     ))}
        //   </Select>
        // </FormControl>
      )

    case FieldType.CHECKBOX:
      return (
        <FormGroup sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1 }}>
          <Typography variant="caption" sx={{ mb: 0.5, px: 0.5 }}>
            {config.label}
          </Typography>
          {(config.options ?? []).map(opt => {
            const arr = Array.isArray(value) ? value : []
            const checked = arr.includes(opt)
            return (
              <FormControlLabel
                key={opt}
                control={
                  <Checkbox
                    size="small"
                    checked={checked}
                    onChange={() => {
                      const next = checked ? arr.filter(v => v !== opt) : [...arr, opt]
                      onChange(next)
                    }}
                  />
                }
                label={<Typography variant="body2">{opt}</Typography>}
              />
            )
          })}
        </FormGroup>
      )

    case FieldType.COLOR_PICKER:
      return (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Typography variant="body2" sx={{ minWidth: 140 }}>
            {config.label}
          </Typography>
          <TextField
            type="color"
            size="small"
            value={value ?? '#000000'}
            fullWidth
            onChange={e => onChange(e.target.value)}
            sx={{ ...denseInputSx, ...compactInputSx }}
          />
        </Box>
      )

    // Basic date (if you use MUI pickers in batch, replace with your DatePicker)
    case FieldType.DATE:
      return (
        <TextField
          label={config.label}
          type="date"
          value={value ?? ''}
          onChange={e => onChange(e.target.value)}
          size="small"
          fullWidth
          sx={{ ...denseInputSx, ...compactInputSx }}
          InputLabelProps={{ shrink: true }}
        />
      )

    default:
      return null
  }
}
export default CreativeField
